import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Button from "../Buttons/Button";
import { cardStyles } from "../VenueCard/cardStyles";

// Edit Venue > Floor plan: which map guests see, the 3D room's id in the floor plan API,
// and which tier each of the room's tables books. The room itself is drawn in the builder.
const FP_API = "https://zona-floorplan.netlify.app";
const BUILDER = "https://getzona.app/floorplan-builder-tool";
const ROOM_ID = /^[a-z0-9][a-z0-9-]{2,47}$/;
const TABLE_ID = /^[A-Za-z0-9_-]{1,40}$/; // same rule as the server

const slugify = (name) =>
  String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

// The server's rule: "T1/2" and " t1/2 " match, "T1 / 2" doesn't.
const tierKey = (name) => String(name || "").trim().toLowerCase().replace(/\s+/g, " ");

const picksFrom = (links) =>
  Object.fromEntries((links || []).map((l) => [l.table_id, { tier_id: l.tier_id || null, tier_name: l.tier_name || "" }]));

const samePick = (a, b) =>
  (!a && !b) || (!!a && !!b && (a.tier_id || null) === (b.tier_id || null) && tierKey(a.tier_name) === tierKey(b.tier_name));

export default function VenueFloorPlan({ venueId, venueName }) {
  const [state, setState] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [roomInput, setRoomInput] = useState("");
  const [roomTables, setRoomTables] = useState(null);
  const [roomNote, setRoomNote] = useState("");
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState("");

  // Picks reset only on load, after saving links, and when the room changes.
  const apply = useCallback((data, { keepPicks = false } = {}) => {
    setState(data);
    setRoomInput(data.room_id || "");
    if (!keepPicks) setPicks(picksFrom(data.links));
  }, []);

  const load = useCallback(() => {
    return api
      .get(`/admin/floor-plan/${venueId}`)
      .then(({ data }) => {
        apply(data);
        setLoadState("loaded");
      })
      .catch((err) => {
        const status = err?.response?.status;
        setLoadState(status === 404 || status === 501 ? "unavailable" : "failed");
      });
  }, [venueId, apply]);

  useEffect(() => {
    load();
  }, [load]);

  // The table list comes from the saved room itself, so a redraw shows up here.
  const roomId = state?.room_id || "";
  useEffect(() => {
    let current = true;
    setRoomTables(null);
    setRoomNote("");
    if (!roomId) return undefined;
    fetch(`${FP_API}/v1/rooms/${encodeURIComponent(roomId)}`, { cache: "no-store" })
      .then((r) => {
        if (r.status === 404) throw new Error("This room hasn't been saved yet. Open it in the builder, draw it, then Save to ZONA.");
        if (!r.ok) throw new Error("The room didn't load. Try again in a minute.");
        return r.json();
      })
      .then((layout) => {
        if (current) setRoomTables((layout.items || []).filter((it) => it && it.t === "table"));
      })
      .catch((e) => {
        if (current) setRoomNote(e.message);
      });
    return () => {
      current = false;
    };
  }, [roomId]);

  const tiers = useMemo(() => state?.tiers || [], [state]);
  const tierById = useMemo(() => new Map(tiers.map((t) => [t.tier_id, t])), [tiers]);
  const tierByKey = useMemo(() => new Map(tiers.map((t) => [tierKey(t.tier_name), t])), [tiers]);
  // Saved tier id first, then the exact name.
  const optionFor = useCallback(
    (pick) => (pick ? tierById.get(pick.tier_id) || tierByKey.get(tierKey(pick.tier_name)) || null : null),
    [tierById, tierByKey],
  );

  const saved = useMemo(() => picksFrom(state?.links), [state]);
  const dirty = (roomTables || []).some((it) => !samePick(picks[it.id], saved[it.id]));

  const run = async (what, fn) => {
    setBusy(what);
    try {
      await fn();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("The floor plan changed in another tab. It's reloaded; check it and save again.");
        await load();
      } else {
        toast.error(err?.response?.data?.message || "That didn't save. Try again.");
      }
    } finally {
      setBusy("");
    }
  };

  const confirmDiscard = () => !dirty || window.confirm("You have table links that aren't saved. Continue without saving them?");

  const setMode = (mode) => {
    if (!confirmDiscard()) return;
    run("mode", async () => {
      const { data } = await api.put(`/admin/floor-plan/${venueId}/mode`, { mode });
      apply(data, { keepPicks: true });
      toast.success(mode === "3d" ? "Guests see the 3D map." : "Guests see the venue's image.");
    });
  };

  const saveRoom = () => {
    const id = roomInput.trim().toLowerCase();
    if (id && !ROOM_ID.test(id)) {
      toast.error("A room id is 3 to 48 lowercase letters, digits or dashes.");
      return;
    }
    const changing = id !== (state.room_id || "");
    if (changing && !confirmDiscard()) return;
    if (changing && state.links?.length && !window.confirm("A different room clears this venue's table links. Continue?")) return;
    if (!id && state.mode === "3d" && !window.confirm("Removing the room puts guests back on the venue's image. Continue?")) return;
    run("room", async () => {
      const { data } = await api.put(`/admin/floor-plan/${venueId}/room`, { room_id: id || null });
      apply(data, { keepPicks: !changing });
      toast.success(id ? "Room saved." : "Room removed.");
    });
  };

  const sendLinks = (links, what, done) =>
    run(what, async () => {
      const { data } = await api.put(`/admin/floor-plan/${venueId}/links`, { links });
      apply(data);
      toast.success(done);
    });

  const saveLinks = () => {
    const bad = (roomTables || []).map((it) => String(it.id)).filter((id) => !TABLE_ID.test(id));
    if (bad.length) {
      toast.error(`These table ids can't be linked: ${bad.join(", ")}. Re-add those tables in the builder.`);
      return;
    }
    const links = (roomTables || [])
      .filter((it) => !samePick(picks[it.id], saved[it.id]))
      .map((it) => {
        const pick = picks[it.id];
        const option = optionFor(pick);
        if (option) return { table_id: it.id, tier_id: option.tier_id };
        // "Not linked" is an explicit unlink; the server treats an empty entry as no change.
        return pick && pick.tier_name ? { table_id: it.id, tier_name: pick.tier_name } : { table_id: it.id, unlink: true };
      });
    if (!links.length) {
      toast.info("Nothing to save.");
      return;
    }
    sendLinks(links, "links", "Table links saved.");
  };

  if (loadState === "unavailable") {
    return (
      <div style={styles.card}>
        <div style={styles.label}>Floor plan</div>
        <p style={{ ...styles.help, margin: 0 }}>Floor plan isn't available yet.</p>
      </div>
    );
  }
  if (loadState === "failed") {
    return (
      <div style={styles.card}>
        <div style={styles.label}>Floor plan</div>
        <p style={{ ...styles.help, color: "#ff6b6b", margin: 0 }}>Couldn't load the floor plan settings. Reload the page.</p>
      </div>
    );
  }
  if (!state) return null;

  const builderId = roomInput.trim().toLowerCase() || slugify(venueName);
  const unlinked = (roomTables || []).filter((it) => !picks[it.id]).length;
  const roomIds = new Set((roomTables || []).map((it) => String(it.id)));
  const orphans = roomTables ? (state.links || []).filter((l) => !roomIds.has(l.table_id)) : [];
  // Saved names with no upcoming night still show, so saving doesn't drop them.
  const stale = [...new Set(Object.values(picks).filter((p) => p && !optionFor(p)).map((p) => p.tier_name))].filter(Boolean);

  return (
    <div style={styles.card}>
      <div style={styles.label}>Floor plan</div>
      <p style={styles.help}>
        What guests see above the table list. The 3D map needs a room; removing the room puts the venue back on
        its image, and with neither guests see the list alone.
      </p>

      <div style={styles.row}>
        {[
          ["image", "Their image"],
          ["3d", "3D map"],
        ].map(([mode, text]) => (
          <button
            key={mode}
            type="button"
            onClick={() => state.mode !== mode && setMode(mode)}
            disabled={busy === "mode" || (mode === "3d" && !state.room_id)}
            title={mode === "3d" && !state.room_id ? "Save a room id first" : undefined}
            aria-pressed={state.mode === mode}
            style={{ ...styles.toggle, ...(state.mode === mode ? styles.toggleOn : null) }}
          >
            {text}
          </button>
        ))}
      </div>
      {!state.room_id && <p style={{ ...styles.help, margin: "10px 0 0" }}>Save a room id below to turn on the 3D map.</p>}

      <label style={{ ...styles.field, marginTop: 16 }}>
        <span style={styles.fieldLabel}>Room id</span>
        <div style={styles.row}>
          <input
            type="text"
            placeholder={slugify(venueName) || "venue-name"}
            value={roomInput}
            onChange={(e) => setRoomInput(e.target.value)}
            style={{ ...styles.input, flex: "1 1 220px" }}
          />
          <Button
            primaryBtn
            onClick={saveRoom}
            disabled={busy === "room" || roomInput.trim().toLowerCase() === (state.room_id || "")}
          >
            {busy === "room" ? "Saving…" : "Save room id"}
          </Button>
          <a href={`${BUILDER}?room=${encodeURIComponent(builderId)}`} target="_blank" rel="noopener noreferrer" style={styles.link}>
            Open in builder
          </a>
        </div>
      </label>
      <p style={{ ...styles.help, margin: "8px 0 0" }}>
        In the builder, drag the tables into place and tap Save to ZONA. Moving or renaming a table keeps its link.
      </p>

      {state.room_id && (
        <div style={{ marginTop: 18 }}>
          <div style={styles.fieldLabel}>
            Tables {roomTables ? `· ${roomTables.length} in the room${unlinked ? `, ${unlinked} not linked` : ""}` : ""}
          </div>
          {roomNote && <p style={{ ...styles.help, margin: "8px 0 0" }}>{roomNote}</p>}
          {roomTables && !tiers.length && (
            <p style={{ ...styles.help, margin: "8px 0 0" }}>This venue has no table tiers yet, so there's nothing to link.</p>
          )}
          {roomTables && roomTables.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {roomTables.map((it) => {
                const pick = picks[it.id];
                const option = optionFor(pick);
                const value = option ? option.tier_id : pick && pick.tier_name ? `name:${pick.tier_name}` : "";
                return (
                  <div key={it.id} style={styles.tableRow}>
                    <span style={styles.tableName}>
                      {it.label || it.id}
                      <span style={styles.tableId}> {it.id}</span>
                    </span>
                    <select
                      value={value}
                      onChange={(e) => {
                        const v = e.target.value;
                        const t = tierById.get(v);
                        const next = t
                          ? { tier_id: t.tier_id, tier_name: t.tier_name }
                          : v.startsWith("name:")
                            ? { tier_id: null, tier_name: v.slice(5) }
                            : null;
                        setPicks({ ...picks, [it.id]: next });
                      }}
                      style={{ ...styles.input, padding: "8px 10px", flex: "0 1 260px", ...(pick ? null : styles.unlinked) }}
                    >
                      <option value="">Not linked</option>
                      {tiers.map((t) => (
                        <option key={t.tier_id} value={t.tier_id}>
                          {t.tier_name}
                          {t.price != null ? ` · $${Number(t.price).toLocaleString("en-US")}` : " · price on request"}
                        </option>
                      ))}
                      {stale.map((n) => (
                        <option key={`stale-${n}`} value={`name:${n}`}>
                          {n} · no upcoming nights
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
              <p style={{ ...styles.help, margin: "10px 0 0" }}>
                Tables sold as a pair (like T1/2) link to the same tier. A table that isn't linked shows greyed out.
              </p>
              <div style={{ marginTop: 12 }}>
                <Button primaryBtn onClick={saveLinks} disabled={busy === "links" || !dirty}>
                  {busy === "links" ? "Saving…" : "Save table links"}
                </Button>
              </div>
            </div>
          )}

          {orphans.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={styles.fieldLabel}>Links for tables no longer in the room</div>
              {orphans.map((l) => (
                <div key={l.table_id} style={styles.tableRow}>
                  <span style={styles.tableName}>
                    {l.table_id}
                    <span style={styles.tableId}> {l.tier_name}</span>
                  </span>
                  <Button
                    onClick={() => sendLinks([{ table_id: l.table_id, unlink: true }], `unlink-${l.table_id}`, "Link removed.")}
                    disabled={busy === `unlink-${l.table_id}`}
                  >
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  ...cardStyles,
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  toggle: {
    background: "#050505",
    border: "1px solid #1a1a1a",
    borderRadius: 10,
    color: "rgba(255,255,255,0.7)",
    padding: "10px 16px",
    fontSize: 14,
    cursor: "pointer",
  },
  toggleOn: { borderColor: "#4F30EA", background: "rgba(79,48,234,0.18)", color: "#fff" },
  link: { color: "#9D8CF7", fontSize: 14, textDecoration: "none" },
  tableRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "8px 0",
    borderBottom: "1px solid #141414",
  },
  tableName: { fontSize: 14, color: "#fff" },
  tableId: { fontSize: 12, color: "rgba(255,255,255,0.35)" },
  unlinked: { borderColor: "rgba(255,180,80,0.45)" },
};
